import { useState } from 'react';
import { Box, IconButton, Popover, Typography, Paper, Tooltip, CircularProgress } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import { AVATAR_COLORS } from '../../helpers/avatarHelper';

interface AvatarColorPickerProps {
  currentColor?: string;
  onColorSelect: (color: string) => void;
  isLoading?: boolean;
}

export const AvatarColorPicker = ({ currentColor, onColorSelect, isLoading = false }: AvatarColorPickerProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleColorSelect = (color: string) => {
    onColorSelect(color);
    handleClose();
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <Tooltip title="Change avatar color" placement="top">
        <IconButton
          onClick={handleClick}
          sx={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            bgcolor: 'background.paper',
            boxShadow: 2,
            '&:hover': {
              bgcolor: 'background.paper',
              boxShadow: 3,
            },
            width: 32,
            height: 32,
          }}
          disabled={isLoading}
        >
          {isLoading ? <CircularProgress size={16} /> : <EditIcon sx={{ fontSize: 16 }} />}
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            p: 2,
            borderRadius: 2,
            boxShadow: 3,
          },
        }}
      >
        <Paper elevation={0}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
            Choose Avatar Color
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(5, 1fr)',
              gap: 1.5,
              maxWidth: 240,
            }}
          >
            {AVATAR_COLORS.map(color => (
              <Tooltip key={color} title={color} placement="top">
                <Box
                  onClick={() => handleColorSelect(color)}
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    bgcolor: color,
                    cursor: 'pointer',
                    border: currentColor === color ? '3px solid' : '2px solid transparent',
                    borderColor: currentColor === color ? 'primary.main' : 'transparent',
                    boxShadow: 2,
                    transition: 'all 0.2s ease-in-out',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&:hover': {
                      transform: 'scale(1.15)',
                      boxShadow: 4,
                    },
                  }}
                >
                  {currentColor === color && (
                    <CheckIcon
                      sx={{
                        color: 'white',
                        fontSize: 20,
                        filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))',
                      }}
                    />
                  )}
                </Box>
              </Tooltip>
            ))}
          </Box>
        </Paper>
      </Popover>
    </>
  );
};
